/**
 * Finish your sponsorship: the public sponsor asset page (owner redesign,
 * 2026-10, same colors and type as the rest of the site).
 *
 * Reached from every sponsor email's "Upload sponsor assets" link, which
 * carries only the unguessable fulfillment_token: no embed key, no login.
 *
 * One control per declared fulfillment field. The logo is a single upload
 * box that turns into the logo itself (at its own shape, no tile) with
 * Replace / Remove; pasting a link is tucked behind "Paste a link instead".
 * Text and website fields are plain inputs (websites take "www.site.com").
 * After Save: a short summary and Make changes.
 *
 * Below the form, sponsors who can buy it here also get the Digital Swag Bag
 * offer (the organizer's separate Contact Card link still works on its own).
 */
import { useEffect, useMemo, useState } from 'react';

import {
  getSponsorContactCard,
  getSponsorFulfillment,
  submitSponsorFulfillment,
  type SponsorFulfillmentData,
  type SponsorshipFulfillmentField,
} from '@/api/embed';
import { isInApp } from '@/inApp';
import { SponsorContactCardPage } from '@/pages/SponsorContactCardPage';
import './SponsorFulfillmentPage.css';
import './SponsorContactCardPage.css';

interface Props {
  token: string;
}

// Field types that hold an uploaded asset (vs. a typed value).
const ASSET_TYPES = new Set(['image', 'image_url', 'file']);
// Max raw file size before base64 encoding (~1.5 MB → ~2 MB data URI).
const MAX_FILE_BYTES = 1.5 * 1024 * 1024;

function isAssetField(f: SponsorshipFulfillmentField): boolean {
  return ASSET_TYPES.has((f.type || '').toLowerCase());
}

/** "www.site.com" → "https://www.site.com". Saved sites often lack the
 *  scheme, and a type="url" input rejected them with the browser's own
 *  validation bubble before the form ever submitted. */
function normalizeUrl(raw: string): string {
  const v = raw.trim();
  if (!v || v.startsWith('data:') || /^[a-z][a-z0-9+.-]*:\/\//i.test(v)) return v;
  return `https://${v.replace(/^\/+/, '')}`;
}

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function SponsorFulfillmentPage({ token }: Props) {
  const [data, setData] = useState<SponsorFulfillmentData | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [pasteOpen, setPasteOpen] = useState<Record<string, boolean>>({});
  // The Swag Bag offer (Digital Contact Card) on this same link, when this
  // sponsor can buy it here and hasn't yet.
  const [swagEligible, setSwagEligible] = useState(false);
  const [swagPrice, setSwagPrice] = useState(1000);
  const [addonOpen, setAddonOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    getSponsorContactCard(token)
      .then((d) => {
        if (!alive) return;
        setSwagEligible(!d.already_paid);
        setSwagPrice(d.price_cents);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [token]);

  useEffect(() => {
    let alive = true;
    getSponsorFulfillment(token)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setValues({ ...d.fulfillment_data });
        if (d.fulfillment_status === 'complete') setDone(true);
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(
          err?.response?.status === 404
            ? "This upload link isn't valid anymore. If you think that's a mistake, reply to your confirmation email."
            : 'Could not load your sponsorship. Please try again in a moment.',
        );
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const missingRequired = useMemo(() => {
    if (!data) return false;
    return data.fulfillment_fields.some(
      (f) => f.required && !(values[f.key] ?? '').trim(),
    );
  }, [data, values]);

  const onFile = async (key: string, file: File | undefined) => {
    setFieldError(null);
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setFieldError('That file is too large — please use an image under 1.5 MB.');
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setValues((prev) => ({ ...prev, [key]: dataUrl }));
    } catch {
      setFieldError('Could not read that file. Please try another.');
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);
    if (missingRequired) {
      const missing = data?.fulfillment_fields.find((f) => f.required && !(values[f.key] ?? '').trim());
      setFieldError(missing ? `Add your ${missing.label.replace(/^your\s+/i, '').toLowerCase()} to finish.` : 'Please fill in the missing details.');
      return;
    }
    setSubmitting(true);
    try {
      // URL fields (and pasted logo links) go out with a scheme.
      const out = { ...values };
      for (const f of data?.fulfillment_fields ?? []) {
        const v = out[f.key];
        if (typeof v === 'string' && (f.type === 'url' || isAssetField(f))) out[f.key] = normalizeUrl(v);
      }
      const updated = await submitSponsorFulfillment(token, out);
      setData(updated);
      setValues({ ...updated.fulfillment_data });
      setDone(true);
    } catch {
      setFieldError('Something went wrong saving your assets. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="tn-fulfill">
        <h1 className="tn-fulfill__title">Finish your sponsorship</h1>
        <div role="alert" className="tn-fulfill__error">{loadError}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="tn-fulfill">
        <p className="tn-fulfill__loading">Loading…</p>
      </div>
    );
  }

  // What the logo slot shows: the cleaned copy once the server has made one
  // for this exact upload, else what was picked or pasted.
  const logoSrc = (val: string): string => {
    if (!val) return '';
    const clean = (data.fulfillment_data as Record<string, unknown>)?.logo_clean as
      | { source?: string; url?: string | null }
      | undefined;
    if (clean?.url && clean.source === val) return clean.url;
    return val.startsWith('data:') ? val : normalizeUrl(val);
  };

  // Offline sponsors can add the Swag Bag offer right here (the same form as
  // the organizer's separate Contact Card link). Hidden when they already
  // have it or can't buy it on this link.
  // Never sold inside the app's in-app browser (see src/inApp.ts).
  const addon = swagEligible && !isInApp() ? (
    <section className="tn-fa__addon">
      {addonOpen ? (
        <SponsorContactCardPage token={token} embedded />
      ) : (
        <>
          <div className="tn-fa__addon-title">
            Add to the Digital Swag Bag <span className="tn-fa__addon-price">+{money(swagPrice)}</span>
          </div>
          <p className="tn-fa__addon-desc">
            Put an offer or your contact card in every golfer’s Swag Bag in the Golf Fore Good app. They see it
            during the round and keep it after the event.
          </p>
          <button type="button" className="tn-btn tn-btn--secondary" onClick={() => setAddonOpen(true)}>
            Add an offer
          </button>
        </>
      )}
    </section>
  ) : null;

  if (done) {
    const filled = data.fulfillment_fields.filter((f) => (values[f.key] ?? '').trim());
    return (
      <div className="tn-fulfill">
        <p className="tn-cc__eyebrow">
          {data.tier_name} · {data.event_name}
        </p>
        <div className="tn-fa__check" aria-hidden>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>
        <h1 className="tn-fulfill__title">You’re all set</h1>
        <p className="tn-fulfill__subtitle">
          Thanks, {data.sponsor_name}. Your details will show for {data.event_name}.
        </p>
        {filled.length ? (
          <dl className="tn-fa__summary">
            {filled.map((f) => (
              <div className="tn-fa__summary-row" key={f.key}>
                <dt>{f.label}</dt>
                <dd>
                  {isAssetField(f) ? (
                    <img className="tn-fa__summary-logo" src={logoSrc(values[f.key])} alt="" />
                  ) : (
                    values[f.key].replace(/^https?:\/\//, '')
                  )}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
        <button type="button" className="tn-btn tn-btn--secondary tn-fulfill__editagain" onClick={() => setDone(false)}>
          Make changes
        </button>
        {addon}
      </div>
    );
  }

  return (
    <div className="tn-fulfill">
      <p className="tn-cc__eyebrow">
        {data.tier_name} · {data.event_name}
      </p>
      <h1 className="tn-fulfill__title">Finish your sponsorship</h1>
      <p className="tn-fulfill__subtitle">
        Add your details, {data.sponsor_name}. Golfers see them on the event page and in the app.
      </p>

      <form className="tn-fulfill__form" onSubmit={onSubmit} noValidate>
        {data.fulfillment_fields.map((f) => {
          const val = values[f.key] ?? '';
          const set = (v: string) => setValues((prev) => ({ ...prev, [f.key]: v }));
          if (isAssetField(f)) {
            const src = logoSrc(val);
            const picker = (label: React.ReactNode, className: string) => (
              <label className={className}>
                {label}
                <input
                  className="tn-fa__file"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    void onFile(f.key, e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </label>
            );
            return (
              <div className="tn-field" key={f.key}>
                <span className="tn-field__label">{f.label}</span>
                {src ? (
                  <div className="tn-fa__logo">
                    {/* The logo at its own shape, no tile (the app's look). */}
                    <img src={src} alt={`${f.label} preview`} />
                    <div className="tn-fa__logo-actions">
                      {picker('Replace', 'tn-fa__link')}
                      <button type="button" className="tn-fa__link tn-fa__link--muted" onClick={() => set('')}>
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  picker(
                    <>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <path d="M17 8l-5-5-5 5" />
                        <path d="M12 3v12" />
                      </svg>
                      <span>Upload your {f.label.replace(/^your\s+/i, '').toLowerCase()}</span>
                    </>,
                    'tn-fa__drop',
                  )
                )}
                <span className="tn-field__hint">
                  PNG or JPG. We trim extra space and remove plain white backgrounds so it looks clean.
                </span>
                {pasteOpen[f.key] ? (
                  <input
                    className="tn-field__input"
                    type="text"
                    inputMode="url"
                    autoCapitalize="off"
                    autoCorrect="off"
                    value={val.startsWith('data:') ? '' : val}
                    onChange={(e) => set(e.target.value)}
                    placeholder="Paste an image link"
                  />
                ) : (
                  <button
                    type="button"
                    className="tn-fa__link tn-fa__paste"
                    onClick={() => setPasteOpen((p) => ({ ...p, [f.key]: true }))}
                  >
                    Paste a link instead
                  </button>
                )}
              </div>
            );
          }
          const isUrl = f.type === 'url';
          return (
            <label className="tn-field" key={f.key}>
              <span className="tn-field__label">{f.label}</span>
              {/* Plain text with the URL keyboard: type="url" made the browser
                  reject "www.site.com" (no http://) and block the submit. */}
              <input
                className="tn-field__input"
                type="text"
                inputMode={isUrl ? 'url' : undefined}
                autoCapitalize={isUrl ? 'off' : undefined}
                autoCorrect={isUrl ? 'off' : undefined}
                value={val}
                onChange={(e) => set(e.target.value)}
                placeholder={isUrl ? 'yourbusiness.com' : undefined}
              />
              {f.hint ? <span className="tn-field__hint">{f.hint}</span> : null}
            </label>
          );
        })}

        {fieldError ? <div role="alert" className="tn-fulfill__error">{fieldError}</div> : null}

        <button type="submit" className="tn-btn tn-btn--primary" disabled={submitting}>
          {submitting ? 'Saving…' : 'Save'}
        </button>
        <p className="tn-fa__note">You can come back to this link any time to make changes.</p>
      </form>
      {addon}
    </div>
  );
}
